import { memo } from 'react';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { usePartnershipData } from './usePartnershipData.jsx';

export const PortfolioLinkageChart = memo(function PortfolioLinkageChart({ linkageData: dataProp }) {
  const { linkageData: dataHook, filteredResults } = usePartnershipData();
  const yAxisWidth = useResponsiveYAxisWidth(160);
  const linkageData = dataProp ?? dataHook;

  const noDataFallback = (
    <ChartWrapper title="Programs Linked to This Accelerator">
      <NoChartData message="No portfolio linkage data available" detail="This chart shows how programs within the portfolio link to each other through shared results and contributions. No cross-program linkages were found." />
    </ChartWrapper>
  );

  if (!linkageData || !linkageData.programLinkages) return noDataFallback;

  const chartData = Object.entries(linkageData.programLinkages)
    .map(([name, data]) => ({
      name: name.length > 25 ? name.substring(0, 23) + '...' : name,
      fullName: name,
      results: data.resultCount,
    }))
    .sort((a, b) => b.results - a.results)
    .slice(0, 12);

  if (chartData.length === 0) return noDataFallback;

  return (
    <ChartWrapper title="Programs Linked to This Accelerator" info="Displays the number of results shared between this accelerator and each linked program. Longer bars indicate stronger portfolio linkages. Cross-program links below show the most active bilateral connections across the portfolio." chartData={chartData} sourceResults={filteredResults} exportFilename="portfolio-linkages" description="Displays the number of results shared between this accelerator and each Science Program or fellow Accelerator. Longer bars indicate stronger portfolio linkages. The cross-program links above highlight the most active bilateral connections.">
      <p className="text-sm text-gray-500 -mt-1 mb-2">Each bar shows how many results are shared with each linked program</p>
      <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 40)}>
        <BarChart data={chartData} layout="vertical" margin={{ right: 40 }}>
          <defs>
            <linearGradient id="greenGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#006837" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#3B9B61" stopOpacity={1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} tickCount={9} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 160 ? 9 : 11 }} width={yAxisWidth} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const d = payload[0].payload;
              return (
                <div style={getTooltipStyle()} className="px-3 py-2 text-sm shadow-lg rounded-lg border">
                  <p className="font-semibold" style={{ color: 'var(--tooltip-text, #0f172a)' }}>{d.fullName}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--tooltip-text, #6b7280)' }}>
                    {d.results} shared result{d.results !== 1 ? 's' : ''}
                  </p>
                </div>
              );
            }}
          />
          <Bar dataKey="results" fill="url(#greenGradient)" radius={[0, 4, 4, 0]} name="Results">
            <LabelList
              dataKey="results"
              position="right"
              content={({ x, y, width, height, value }) => (
                <text x={x + width + 4} y={y + height / 2} {...LABEL_STYLE} dominantBaseline="middle">
                  {value}
                </text>
              )}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {linkageData.crossLinks && linkageData.crossLinks.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Top Cross-Program Links</h5>
          <div className="flex flex-wrap gap-2">
            {linkageData.crossLinks
              .sort((a, b) => b.count - a.count)
              .slice(0, 6)
              .map((link, idx) => (
                <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-800 text-xs rounded-full border border-green-200">
                  <span className="font-medium">{link.source.split(' - ')[0]}</span>
                  <span className="text-green-400">&harr;</span>
                  <span className="font-medium">{link.target.split(' - ')[0]}</span>
                  <span className="bg-green-200 text-green-900 rounded-full px-1.5 py-0.5 text-[10px] font-bold ml-1">{link.count}</span>
                </span>
              ))}
          </div>
        </div>
      )}
    </ChartWrapper>
  );
});
